(() => {
  if (customElements.get('maxler-protein-product')) return;

  function photoPicker(select, options) {
    class PhotoFlavourPicker extends window.MaxlerFlavourPicker {
      photoFrame(value) {
        const frame = document.createElement('span');
        frame.className = 'protein-picker-photo';
        frame.setAttribute('aria-hidden', 'true');
        const photoData = this.context.flavourPickerPhotoData(value);
        const photo = photoData?.src;
        if (photo) {
          const image = document.createElement('img');
          Object.assign(image, {src: photo, alt: '', width: 38, height: 38, decoding: 'async'});
          image.style.objectFit = photoData.kind === 'product' ? 'contain' : 'cover';
          frame.dataset.imageKind = photoData.kind || 'flavour';
          image.style.objectPosition = photoData.focal || '50% 50%';
          frame.append(image);
        }
        return frame;
      }
      sync() {
        super.sync();
        if (!this.value || this.signal.aborted) return;
        const root = this.context;
        this.value.replaceChildren();
        this.value.append(this.photoFrame(this.select.value));
        const caption = document.createElement('span');caption.className = 'protein-picker-caption';
        const name = document.createElement('strong');name.textContent = this.select.selectedOptions[0] ? (root.flavourProfile(this.select.value)?.name || this.optionLabel(this.select.selectedOptions[0])) : '';
        const count = document.createElement('small');count.textContent = root.text('flavourCount',{count:[...this.select.options].filter(o=>o.value).length});
        caption.append(name,count);this.value.append(caption);
        this.trigger.classList.toggle('is-unavailable', !root.flavourVariants(this.select.value).some(variant => root.sizeAvailable(variant)));
        this.rows.forEach(row => { const selected = row.dataset.value === this.select.value;row.setAttribute('aria-selected',String(selected));row.classList.toggle('is-selected',selected);row.lastElementChild.textContent = selected ? '✓' : ''; });
      }
      render() {
        super.render();
        for (const row of this.rows) {
          const option = [...this.select.options].find(o=>o.value===row.dataset.value);
          const title = row.firstElementChild;title.className = 'protein-picker-option-name';
          const name = document.createElement('span');name.textContent = this.context.flavourProfile(row.dataset.value)?.name || title.textContent;
          title.replaceChildren();
          title.append(this.photoFrame(row.dataset.value));
          const caption = document.createElement('span');caption.className='protein-picker-option-caption';caption.append(name);
          const unavailable=option?.dataset.qpAvailability === 'unavailable';
          if(unavailable){const badge=document.createElement('span');badge.dataset.proteinSoldOutBadge='';badge.textContent=this.context.labels.soldOut;caption.append(badge);}
          title.append(caption);
          row.lastElementChild.dataset.selectedTick='';
          row.classList.toggle('is-unavailable',unavailable);
        }
        this.panel.querySelectorAll('.maxler-qp-picker-group-label').forEach(label=>label.classList.add('visually-hidden'));
        this.panel.querySelectorAll('[role="group"]').forEach(group => {
          const value = group.querySelector('[data-qp-picker-option]')?.dataset.value;
          group.dataset.qpGroup = [...this.select.options].find(o=>o.value===value)?.dataset.qpAvailability || 'available';
        });
      }
      choose(value) {
        const option = [...this.select.options].find(o=>o.value===value);
        if (!this.eligible(option) || this.signal.aborted) return;
        if (!this.context.chooseFlavour(value)) return;
        this.close();this.sync();
        if (!this.context.querySelector('dialog[open]')) this.trigger.focus({preventScroll:true});
      }
    }
    return new PhotoFlavourPicker(select,options);
  }

  class ProteinProduct extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      const signal = this.signal;
      // Let child purchase components connect before the parent reads their state.
      const ready = () => queueMicrotask(() => { if (!signal.aborted) this.start(); });
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, {once: true, signal});
      else ready();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.thumbObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
      delete this.dataset.enhanced;
    }
    start() {
      if (this.dataset.enhanced) return;
      const read = selector => JSON.parse(this.querySelector(selector)?.textContent || '[]');
      this.variants = read('[data-variant-json]');
      this.meta = new Map(read('[data-protein-variant-data]').map(row => [String(row.id), row]));
      this.labels = read('[data-protein-labels]');
      this.flavourLibrary = new Map(read('script[data-protein-flavour-library]').map(row => [String(row.id), row]));
      this.flavourLabels = {chooseSize: this.labels.refine?.chooseSize || '', back: '', soldOut: this.labels.soldOut, ...read('script[data-protein-flavour-labels]')};
      this.selects = [...this.querySelectorAll('[data-variant-input]')];
      this.current = this.variants.find(v => String(v.id) === (this.querySelector('[data-product-select]')?.value || this.dataset.variantId)) || null;
      this.complete = Number(this.dataset.variantCount) === this.variants.length;
      this.sizeRevision = null;
      this.form = this.querySelector('.product-single__form');
      this.quantity = this.querySelector('input[name="quantity"]');
      this.error = this.querySelector('[data-protein-error]');
      this.busy = false;
      this.addEventListener('variantChange', event => {
        if (!Object.prototype.hasOwnProperty.call(event.detail || {}, 'variant')) return;
        const notification = this.querySelector('[data-protein-notify-dialog][open]');
        if (notification) this.closeDialog(notification);
        this.current = event.detail.variant;
        queueMicrotask(() => { if (!this.signal.aborted) this.render(true); });
      }, {signal: this.signal});
      this.addEventListener('change', event => {
        if (!this.complete && event.target.matches('[data-variant-input]')) {
          event.stopImmediatePropagation();
          const values = this.selects.map(select => select.selectedOptions[0]?.dataset.optionValueId);
          if (values.every(Boolean)) {
            const url = new URL(location.href);
            url.searchParams.delete('variant');
            url.searchParams.set('option_values', values.join(','));
            location.assign(url.href);
          }
          return;
        }
      }, {capture: true, signal: this.signal});
      this.addEventListener('change', event => {
        if (event.target.matches('[data-purchase-mode], input[name="quantity"]')) this.render(event.target.matches('[data-purchase-mode]'));
      }, {signal: this.signal});
      this.addEventListener('click', event => this.click(event), {signal: this.signal});
      this.form?.addEventListener('submit', event => this.submit(event), {capture: true, signal: this.signal});
      this.querySelectorAll('dialog').forEach(dialog => dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.closeDialog(dialog);
      }, {signal: this.signal}));
      this.flavourSelect = this.querySelector('[data-protein-flavour]');
      this.interest = this.querySelector('maxler-variant-interest');
      this.choicesSupported = this.complete && this.selects.length <= 2;
      this.dataset.choicesEnhanced = String(this.choicesSupported);
      this.sortSizes();
      this.sizeChoices = [...this.querySelectorAll('[data-protein-choice]')];
      this.querySelectorAll('[data-protein-size-group]').forEach(group=>{group.hidden=!this.choicesSupported;});
      if (this.flavourSelect && this.choicesSupported && window.MaxlerFlavourPicker) {
        this.refreshNativeOptions();
        this.picker = photoPicker(this.flavourSelect,{signal:this.signal,context:this,labels:{
          available:this.dataset.us==='true' ? this.labels.flavourLabel : this.labels.flavourGroupAvailable,
          unavailable:this.labels.flavourGroupUnavailable,empty:this.labels.flavourEmpty,hint:''
        }});
      }
      this.dataset.enhanced = 'true';
      this.thumbObserver = new MutationObserver(() => this.syncThumbnails());
      const thumbs = this.querySelector('[data-product-thumbs]');
      if (thumbs) this.thumbObserver.observe(thumbs, {subtree: true, attributes: true, attributeFilter: ['class']});
      this.resizeObserver = new ResizeObserver(() => this.syncThumbnails());
      if (thumbs) this.resizeObserver.observe(thumbs);
      this.render(false);
      if (this.querySelector('maxler-purchase-options')) {
        const signal=this.signal;
        customElements.whenDefined('maxler-purchase-options').then(()=>queueMicrotask(()=>{if(!signal.aborted)this.compactPurchase();}));
      }
      this.sticky = window.MaxlerProteinSticky?.(this, {signal: this.signal});
      this.resumeInterestResult();
    }
    money(amount) {
      return window.theme?.Currency?.formatMoney(amount, window.theme.settings.moneyFormat) || new Intl.NumberFormat(document.documentElement.lang, {style: 'currency', currency: this.dataset.currency}).format(amount / 100);
    }
    selections() { return this.selects.map(select => select.value); }
    combination(index, value) {
      const values = this.selections();
      values[index] = value;
      return this.variants.find(variant => variant.options.every((option, i) => option === values[i]));
    }
    render(updateUrl) {
      const variant = this.current;
      const meta = this.meta.get(String(variant?.id));
      const purchase = this.querySelector('maxler-purchase-options');
      const planId = String(purchase?.currentPlanId || purchase?.querySelector('[data-selling-plan-input]')?.value || '');
      const plan = meta?.plans.find(plan => String(plan.id) === planId);
      const price = plan?.price ?? meta?.price;
      if (Number.isFinite(price)) this.querySelector('[data-product-price]').textContent = this.money(price);
      const autoSubtitle = this.querySelector('[data-protein-auto-subtitle]');
      if (autoSubtitle) autoSubtitle.textContent = meta?.summary?.whey_blend ? this.text('wheyBlend') : autoSubtitle.dataset.fallback;
      this.querySelectorAll('[data-protein-whey-benefits] > li:not(:first-child)').forEach(item => { item.hidden = !meta?.summary?.whey_blend; });
      const primaryBenefit = this.querySelector('[data-protein-primary-benefit]');
      if (primaryBenefit) {
        const facts = meta?.summary;
        primaryBenefit.textContent = facts?.protein_per_serving && facts?.serving_size
          ? this.labels.proteinSummary.replace('__protein__', facts.protein_per_serving).replace('__serving__', facts.serving_size) + (facts.protein_qualifier ? ` ${this.labels.dryMatter}` : '')
          : primaryBenefit.dataset.fallback;
      }
      const serving = this.querySelector('[data-protein-serving-price]');
      serving.hidden = !(meta?.servings > 0 && Number.isFinite(price));
      if (!serving.hidden) serving.textContent = this.text('servingPriceBadge',{price:this.money(Math.round(price / meta.servings))});
      const unit = plan ? plan.unit_price : meta?.unit_price;
      const unitWrap = this.querySelector('[data-unit-price-wrapper]');
      unitWrap.classList.toggle('visually-hidden', !(unit > 0 && meta?.unit_base));
      if (unit > 0 && meta?.unit_base) {
        unitWrap.querySelector('[data-unit-price]').textContent = this.money(unit);
        unitWrap.querySelector('[data-unit-base]').textContent = meta.unit_base.trim().replace(/^1\s+/,'');
      }
      this.refreshNativeOptions();
      if (this.choicesSupported) this.renderSizes();
      this.picker?.sync();
      const orderable=this.isPurchasable(variant);
      this.querySelector('[data-protein-stock-label]').textContent=variant ? orderable ? this.text('inStock') : this.labels.soldOut : this.labels.missing;
      const delivery=this.querySelector('[data-protein-delivery-estimate]');
      if(delivery)delivery.hidden=!orderable||delivery.dataset.eligible!=='true'||this.dataset.us==='true';
      this.querySelectorAll('[data-protein-retailer]').forEach(row => { row.hidden = row.dataset.proteinRetailer !== String(variant?.id); });
      this.updateQuantity(meta);
      const add = this.form?.querySelector('[name="add"]');
      const invalidPlan = meta?.requires_plan && !plan;
      if (add) add.disabled = this.busy || !variant?.available || invalidPlan || Boolean(purchase?.purchaseIsBlocked?.()) || this.quantityBlocked;
      const addText = add?.querySelector('[data-add-to-cart-text]');
      if (addText) addText.textContent = this.busy ? this.labels.adding : variant && !variant.available ? this.labels.soldOut : addText.dataset.defaultText;
      const offerNote = this.querySelector('[data-protein-offer-note]');
      if (offerNote) offerNote.hidden = !this.isPurchasable(variant);
      this.compactPurchase();
      this.syncNotification(variant);
      this.renderFlavourNotice();
      if (updateUrl) {
        const url = new URL(location.href);
        if (variant) url.searchParams.set('variant', variant.id);
        else url.searchParams.delete('variant');
        url.searchParams.delete('option_values');
        if (variant && plan) url.searchParams.set('selling_plan', plan.id);
        else url.searchParams.delete('selling_plan');
        history.replaceState(history.state, '', url);
      }
      this.syncThumbnails();
    }
    sortSizes() {
      const factors = {kg:1000,g:1,mg:0.001,lb:453.59237,oz:28.349523125};
      this.querySelectorAll('[data-protein-size-group]').forEach(group=>{
        const rows = [...group.querySelectorAll('[data-protein-choice]')].map((node,index)=>{
          const match=node.dataset.proteinChoice.match(/(\d+(?:[.,]\d+)?)\s*(kg|mg|g|lb|oz)\b/i);
          return {node,index,weight:match ? Number(match[1].replace(',','.'))*factors[match[2].toLowerCase()] : null};
        });
        if(rows.every(row=>row.weight!==null))rows.sort((a,b)=>a.weight-b.weight||a.index-b.index);
        rows.forEach(row=>group.querySelector('[data-protein-cards]').append(row.node));
      });
    }
    refreshNativeOptions() {
      if (!this.complete) return;
      this.selects.forEach((select,index)=>[...select.options].forEach(option=>{
        if(!option.value)return;
        const flavour=select===this.flavourSelect;
        const matches=flavour ? this.flavourVariants(option.value) : [this.combination(index,option.value)].filter(Boolean);
        option.disabled=matches.length===0;
        option.dataset.qpAvailability=!matches.length ? 'missing' : (this.dataset.us==='true' && flavour) || matches.some(v=>this.isPurchasable(v)) ? 'available' : 'unavailable';
      }));
    }
    sizeLabel(value) {
      const match=value.match(/(\d+(?:[.,]\d+)?)\s*(kg|mg|g|lb|oz)\b/i);
      return match ? `${match[1]} ${match[2]}` : value;
    }
    sizeCard(button,variant,value) {
      const meta=this.meta.get(String(variant?.id));
      const set=(attribute,text,hidden=false)=>{let span=button.querySelector(`[${attribute}]`);if(!span){span=document.createElement('span');span.setAttribute(attribute,'');button.append(span);}span.textContent=text;span.hidden=hidden;};
      set('data-protein-size-label',this.sizeLabel(value));
      set('data-protein-size-servings',meta?.servings===1 ? this.text('oneServing') : this.text('sizeServings',{count:meta?.servings}),!(meta?.servings>0));
      set('data-protein-size-status',this.labels.soldOut,!variant||this.sizeAvailable(variant));
    }
    sizeAvailable(variant) {
      return Boolean(variant&&(this.dataset.us==='true'||this.isPurchasable(variant)));
    }
    renderSizes() {
      const flavour=this.flavourSelect?.value || '';
      this.querySelectorAll('[data-protein-size-group]').forEach(group=>{
        const index=Number(group.dataset.proteinSizeGroup),primary=group.querySelector('[data-protein-cards]'),extra=group.querySelector('[data-protein-extra-cards]'),unused=group.querySelector('[data-protein-size-unused]'),more=group.querySelector('.protein-size-more');
        const rows=this.sizeChoices.filter(b=>Number(b.dataset.optionIndex)===index).map(button=>({button,variant:this.combination(index,button.dataset.proteinChoice)}));
        const available=rows.filter(row=>this.sizeAvailable(row.variant));
        const unavailable=rows.filter(row=>row.variant&&!this.sizeAvailable(row.variant));
        rows.forEach(({button,variant})=>{
          const orderable=this.sizeAvailable(variant),selected=Boolean(variant&&String(variant.id)===String(this.current?.id));
          button.hidden=!variant;button.disabled=!variant;button.setAttribute('aria-pressed',String(selected));button.classList.toggle('is-selected',selected);button.classList.toggle('is-unavailable',Boolean(variant&&!orderable));
          this.sizeCard(button,variant,button.dataset.proteinChoice);
          button.removeAttribute('aria-label');
          if(!variant)unused.append(button);else if(orderable||available.length===0)primary.append(button);else extra.append(button);
        });
        more.hidden=available.length===0||unavailable.length===0;
        group.dataset.sizeCount=String(available.length||unavailable.length);
        group.style.setProperty('--protein-size-columns',String(Math.max(1,Math.min(3,available.length||unavailable.length))));
        extra.style.setProperty('--protein-size-columns',String(Math.max(1,Math.min(3,unavailable.length))));
        if(this.sizeRevision!==flavour||more.hidden)more.open=false;
        more.querySelector('[data-protein-other-sizes-label]').textContent=this.text('otherSizes',{count:unavailable.length});
        const selected=this.current?.options[index];
        const label=this.querySelector(`[data-protein-selected-size="${index}"]`);
        if(label)label.textContent=selected&&!this.sizeAvailable(this.current) ? ` · ${this.sizeLabel(selected)}` : '';
      });
      this.sizeRevision=flavour;
    }
    flavourVariants(value) {
      const index=this.selects.indexOf(this.flavourSelect);
      return this.variants.filter(v=>v.options[index]===value);
    }
    flavourProfile(value) {
      const option = [...(this.flavourSelect?.options || [])].find(option => option.value === value);
      return this.flavourLibrary.get(String(option?.dataset.optionValueId));
    }
    flavourPickerPhotoData(value) {
      const image = this.flavourProfile(value)?.image;
      return image && image.kind !== 'product'
        ? {src: image.thumb || image.src, kind: 'flavour', focal: image.focal || '50% 50%'}
        : this.flavourPhotoData(value);
    }
    flavourPhoto(value) {
      return this.flavourPhotoData(value)?.src || null;
    }
    flavourPhotoData(value) {
      const profile=this.flavourProfile(value),photo=profile?.image;
      if(photo?.source==='theme')return{src:photo.thumb,kind:'flavour',focal:photo.focal};
      const repeated=photo&&[...this.flavourLibrary.values()].some(r=>r.nativeValue!==value&&r.image?.src===photo.src);
      if(!photo||repeated){
        const variants=this.flavourVariants(value),selected=this.combination(this.selects.indexOf(this.flavourSelect),value);
        const variant=selected?.featured_image?selected:variants.find(v=>v.available&&v.featured_image)||variants.find(v=>v.featured_image);
        if(variant?.featured_image?.src){
          const thumbnail=new URL(variant.featured_image.src,location.origin);
          thumbnail.searchParams.set('width','96');
          return{src:thumbnail.href,kind:'product',focal:'50% 50%'};
        }
      }
      return photo?{src:photo.thumb,kind:photo.kind||'flavour',focal:photo.focal}:null;
    }
    chooseFlavour(value) {
      const index = this.selects.indexOf(this.flavourSelect);
      const variants = this.flavourVariants(value);
      const exact = this.combination(index, value);
      const variant = this.sizeAvailable(exact) ? exact : variants.find(candidate => this.sizeAvailable(candidate)) || exact || variants[0];
      if (!variant) return false;
      const pack = current => current?.options.filter((_, position) => position !== index).join(' / ') || '';
      const previousSize = pack(this.current), selectedSize = pack(variant);
      this.flavourSizeNotice = previousSize !== selectedSize ? {
        variantId: String(variant.id),
        text: this.flavourLabels.sizeNotice.replaceAll('[flavour]', this.flavourProfile(value)?.name || value).replaceAll('[previous_size]', previousSize).replaceAll('[size]', selectedSize)
      } : null;
      this.selectVariant(variant.id, {focus:false});
      if (String(this.current?.id) !== String(variant.id)) this.flavourSizeNotice = null;
      this.renderFlavourNotice();
      return String(this.current?.id) === String(variant.id);
    }
    renderFlavourNotice() {
      if (this.flavourSizeNotice?.variantId !== String(this.current?.id)) this.flavourSizeNotice = null;
      const notice = this.querySelector('[data-protein-flavour-size-notice]');
      if (notice) { notice.textContent = this.flavourSizeNotice?.text || ''; notice.hidden = !notice.textContent; }
    }
    compactPurchase() {
      const purchase = this.querySelector('maxler-purchase-options');
      const source = purchase?.querySelector('[data-protein-policy-source]');
      if (!source) return;
      const frequency = purchase.querySelector('.maxler-purchase-options__group-name, .protein-frequency-heading');
      if (frequency) { frequency.className = 'protein-frequency-heading';frequency.textContent = this.text('subscriptionFrequency');frequency.hidden = purchase.querySelectorAll('[data-purchase-mode="subscription"]').length < 2; }
      purchase.querySelectorAll('[data-purchase-mode="subscription"]').forEach(input => {
        const title = input.closest('label').querySelector('.maxler-purchase-options__choice-title');
        if (title.querySelector('.protein-subscription-note')) return;
        const note = document.createElement('span');
        note.className = 'protein-subscription-note';
        source.childNodes.forEach(node => note.append(node.cloneNode(true)));
        title.append(note);
      });
    }
    variantCost(variant) {
      const meta = this.meta.get(String(variant?.id));
      if (!meta) return null;
      const purchase = this.querySelector('maxler-purchase-options');
      const id = String(purchase?.currentPlanId || '');
      const plan = meta.plans.find(p=>String(p.id)===id) || (meta.requires_plan ? meta.plans[0] : null);
      return {price:plan?.price ?? meta.price,unit:plan?.unit_price ?? meta.unit_price};
    }
    isPurchasable(variant) {
      const meta = this.meta.get(String(variant?.id));
      return Boolean(variant?.available && (!meta?.requires_plan || meta.plans.length));
    }
    text(key, values = {}) {
      let text = this.labels.refine?.[key] || '';
      for (const [name, value] of Object.entries(values)) text = text.replaceAll(`[${name}]`, String(value));
      return text;
    }
    selectVariant(id, {focus = true} = {}) {
      const variant = this.variants.find(v => String(v.id) === String(id));
      if (!variant) return;
      this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
      if (String(this.current?.id) === String(variant.id)) { if (focus) this.picker?.trigger.focus({preventScroll:true}); return; }
      this.selects.forEach((select, index) => { select.value = variant.options[index]; });
      this.selects.at(-1)?.dispatchEvent(new Event('change', {bubbles:true}));
      queueMicrotask(() => {
        if (focus && !this.signal.aborted) this.picker?.trigger.focus({preventScroll:true});
      });
    }
    syncNotification(variant) {
      const orderable = this.isPurchasable(variant);
      const row = this.querySelector('.protein-buy-row');if (row) row.hidden = !orderable;
      const purchase = this.querySelector('.protein-purchase');if (purchase) purchase.hidden = !orderable;
      const notify = this.querySelector('[data-protein-notify-selected]');
      if (notify) { notify.hidden = !variant || orderable;notify.dataset.variantId = variant?.id || ''; }
      const master = this.form?.querySelector('[name="id"]');if (master) master.disabled = !orderable;
    }
    openNotifyVariant(id, opener = null) {
      const variant = this.variants.find(v => String(v.id) === String(id));
      const dialog = this.querySelector('[data-protein-notify-dialog]');
      if (!variant || this.isPurchasable(variant) || !dialog || typeof this.interest?.updateVariant !== 'function') return;
      const previous = this.querySelector('dialog[open]');
      this.dialogOpener = previous ? this.dialogOpener : opener;
      if (previous) previous.close();
      this.interest.updateVariant({...variant,title:variant.title.replaceAll('&amp;', '&'),available:false});
      this.interest.querySelector('details').open = true;
      dialog.querySelector('[data-protein-notify-selection]').textContent = `${this.dataset.productTitle} · ${variant.title.replaceAll('&amp;', '&')}`;
      const returnTo = this.interest.querySelector('[data-interest-return-to]');
      if (returnTo?.value) { const url = new URL(returnTo.value,location.origin);url.searchParams.set('view','protein');returnTo.value = url.pathname+url.search+url.hash; }
      dialog.showModal();this.interest.querySelector('input[type="email"]')?.focus({preventScroll:true});
    }
    resumeInterestResult() {
      const result = this.interest?.querySelector('[data-interest-request-success], .errors');
      if (!result || !this.current || this.isPurchasable(this.current)) return;
      this.openNotifyVariant(this.current.id,this.querySelector('[data-protein-notify-selected]'));
      result.tabIndex = -1;result.focus({preventScroll:true});
    }
    updateQuantity(meta) {
      if (!this.quantity || !meta) return;
      const rule = meta.rule;
      const step = Math.max(1, rule.step || 1);
      const inCart = meta.cart_qty || 0;
      const min = Math.ceil(Math.max(rule.min, inCart + 1) / step) * step - inCart;
      this.remainingMax = rule.max == null ? null : Math.max(0, Math.floor(rule.max / step) * step - inCart);
      this.quantityBlocked = this.remainingMax != null && this.remainingMax < min;
      this.quantity.min = min;
      this.quantity.step = step;
      if (this.remainingMax == null) this.quantity.removeAttribute('max');
      else this.quantity.max = this.remainingMax;
      if (this.quantityVariant !== meta.id && Number(this.quantity.value) < min) this.quantity.value = min;
      this.quantityVariant = meta.id;
      this.querySelector('[data-protein-quantity="-1"]').disabled = Number(this.quantity.value) <= min;
      this.querySelector('[data-protein-quantity="1"]').disabled = this.remainingMax != null && Number(this.quantity.value) + step > this.remainingMax;
    }
    click(event) {
      const choice = event.target.closest('[data-protein-choice]');
      if (choice && !choice.disabled) {
        const index = Number(choice.dataset.optionIndex);
        const match = this.combination(index,choice.dataset.proteinChoice);
        if (match) this.selectVariant(match.id);
      }
      const notification = event.target.closest('[data-protein-notify-variant], [data-protein-notify-selected]');
      if (notification) this.openNotifyVariant(notification.dataset.proteinNotifyVariant || notification.dataset.variantId,notification);
      const quantityButton = event.target.closest('[data-protein-quantity]');
      if (quantityButton && !quantityButton.disabled) {
        const direction = Number(quantityButton.dataset.proteinQuantity);
        this.quantity.value = Math.max(Number(this.quantity.min), Number(this.quantity.value || this.quantity.min) + direction * Number(this.quantity.step));
        this.quantity.dispatchEvent(new Event('change', {bubbles: true}));
      }
      const opener = event.target.closest('[data-protein-dialog]');
      if (opener) {
        const dialog = document.getElementById(opener.dataset.proteinDialog);
        if (dialog?.showModal) {
          event.preventDefault();
          this.dialogOpener = opener;
          this.picker?.close();
          dialog.showModal();
        }
      }
      const closer = event.target.closest('[data-protein-dialog-close]');
      if (closer) this.closeDialog(closer.closest('dialog'));
      if (event.target.tagName === 'DIALOG') this.closeDialog(event.target);
    }
    closeDialog(dialog) {
      dialog?.close();
      if (dialog?.hasAttribute('data-protein-notify-dialog')) this.interest?.updateVariant(null);
      if (this.dialogOpener?.isConnected) this.dialogOpener.focus({preventScroll: true});
    }
    syncThumbnails() {
      const thumbs = [...this.querySelectorAll('[data-product-thumb]')];
      const active = thumbs.find(thumb => thumb.classList.contains('is-active')) || thumbs.find(thumb => thumb.closest('.product__thumb-item')?.classList.contains('is-active'));
      thumbs.forEach(thumb => { if (thumb === active) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current'); });
      if (active && matchMedia('(max-width:989px)').matches) {
        const rail = active.closest('.product__thumbs--scroller');
        if (rail) { rail.scrollTop = 0; rail.scrollLeft = Math.max(0, active.offsetLeft - (rail.clientWidth - active.clientWidth) / 2); }
      }
    }
    async submit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      const variant = this.current;
      const purchase = this.querySelector('maxler-purchase-options');
      const validPlan = !purchase || !purchase.purchaseIsBlocked?.();
      if (!variant?.available || !validPlan || !this.quantity.checkValidity()) {
        this.error.hidden = false;
        this.error.textContent = this.labels.quantity;
        this.quantity.reportValidity();
        return;
      }
      this.error.hidden = true;
      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');
      const button = this.form.querySelector('[name="add"]');
      button.disabled = true;
      button.classList.add('btn--loading');
      const buttonText = button.querySelector('[data-add-to-cart-text]');
      if (buttonText) buttonText.textContent = this.labels.adding;
      try {
        purchase?.syncFormPlan?.();
        const data = new FormData(this.form);
        if (String(data.get('id')) !== String(variant.id)) throw new Error(this.labels.error);
        const response = await fetch(window.theme.routes.cartAdd, {method: 'POST', body: data, credentials: 'same-origin', headers: {'X-Requested-With': 'XMLHttpRequest'}, signal: this.signal});
        const added = await response.json();
        if (!response.ok || added.status >= 400) throw new Error(added.description || added.message || this.labels.error);
        this.form.dispatchEvent(new CustomEvent('ajaxProduct:added', {detail: {product: added, addToCartBtn: button}, bubbles: true}));
        if (window.theme.settings.cartType === 'page') location.assign(window.theme.routes.cartPage);
        const root = window.Shopify?.routes?.root || '/';
        const cartResponse = await fetch(`${root}cart.js`, {credentials: 'same-origin', signal: this.signal}).catch(() => null);
        if (cartResponse?.ok) {
          const cart = await cartResponse.json();
          for (const meta of this.meta.values()) meta.cart_qty = cart.items.filter(item => String(item.variant_id) === String(meta.id)).reduce((sum, item) => sum + item.quantity, 0);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.error.hidden = false;
          this.error.textContent = error.message && error.message !== 'Failed to fetch' ? error.message : this.labels.error;
        }
      } finally {
        this.busy = false;
        this.form.removeAttribute('aria-busy');
        button.classList.remove('btn--loading');
        if (!this.signal.aborted) this.render(false);
      }
    }
  }
  customElements.define('maxler-protein-product', ProteinProduct);
})();
